import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, Quote, Paperclip, ArrowUpRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { SectionHead, QuietEmpty, NewsRow, RecognitionCard, TrainingCard, ResourceTile, ArchiveTeaser, GrowCard, mediaUrl, fmtDay, useOpenContent } from "./agentParts";
import { Award, GraduationCap, Megaphone, FolderOpen, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DashboardSectionsEditor } from "@/components/agent/DashboardSectionsEditor";

const DEFAULT_SECTIONS = ["news", "recognition", "grow", "training", "resources"].map((key) => ({ key }));
import { CandidateSupportWidget } from "@/components/support/CandidateSupportWidget";

function Masthead({ name, account, preview, logo }) {
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <header className="tr-rise flex flex-col md:flex-row md:items-end justify-between gap-2 pb-5 mb-6 border-b border-slate-200" data-testid="agent-masthead">
      <div className="flex flex-col items-start sm:flex-row sm:items-center gap-3 md:gap-5">
        {logo && <img src={mediaUrl(logo)} alt="" className="h-10 md:h-14 w-auto object-contain" data-testid="agent-logo" />}
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-400">{account || "Top-Recruit"} · Agent Home</p>
        <h1 className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-slate-900 mt-1" data-testid="agent-welcome">
          {preview ? "The Agent Home" : `${greet}, ${name || "there"}`}
        </h1>
      </div>
      </div>
      <p className="text-sm text-slate-500" data-testid="agent-issue-date">{today}</p>
    </header>
  );
}

function Leadership({ item, open }) {
  if (!item) return <QuietEmpty icon={Quote} text="A leadership message will appear here when it's published." testId="leadership-empty" />;
  const text = (item.body_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  return (
    <article className="tr-rise relative overflow-hidden rounded-3xl bg-[#062a40] text-white" data-testid="leadership-feature">
      <span className="absolute inset-0 bg-[radial-gradient(ellipse_at_85%_0%,hsl(var(--brand-cyan)/0.35),transparent_55%)]" />
      <span className="absolute inset-0 opacity-[0.06] tr-grain" />
      <div className="relative grid md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="relative min-h-[240px] md:min-h-[380px]">
          {item.image_url ? <img src={mediaUrl(item.image_url)} alt={item.author_name || ""} className="absolute inset-0 w-full h-full object-cover object-[22%_center]" data-testid="leadership-photo" />
            : <div className="absolute inset-0 flex items-center justify-center" data-testid="leadership-monogram"><span className="w-36 h-36 md:w-44 md:h-44 rounded-full border border-white/20 bg-white/5 flex items-center justify-center font-editorial text-5xl md:text-6xl text-white/90">{(item.author_name || "L").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("")}</span></div>}
          <span className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-transparent via-transparent to-[#062a40]/90" />
        </div>
        <div className="relative px-6 py-7 md:px-10 md:py-10 flex flex-col">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-cyan-200/90">{item.category || "A note from leadership"}</p>
          <h2 className="font-editorial text-2xl md:text-[34px] leading-tight mt-2" data-testid="leadership-title">{item.title}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-white/85 line-clamp-6 md:line-clamp-[7]">{item.summary || text}</p>
          <div className="mt-auto pt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            <div>
              <p className="font-editorial italic text-2xl text-white" data-testid="leadership-author">{item.author_name}</p>
              <p className="text-xs text-white/60">{[item.author_title, fmtDay(item.published_on)].filter(Boolean).join(" · ")}</p>
            </div>
            <div className="flex gap-2 ml-auto">
              <button onClick={() => open(item)} data-testid="leadership-read" className="rounded-full bg-white text-[#062a40] text-sm font-semibold px-4 py-2 inline-flex items-center gap-1.5 hover:bg-cyan-50 transition-colors">
                Read the full message <ArrowUpRight className="w-4 h-4" />
              </button>
              {item.cta_url && <a href={item.cta_url} target="_blank" rel="noreferrer" data-testid="leadership-cta" className="rounded-full border border-white/30 text-sm font-semibold px-4 py-2 hover:bg-white/10 transition-colors">{item.cta_label || "Learn more"}</a>}
            </div>
          </div>
          {item.attachments?.length > 0 && <p className="mt-3 text-xs text-white/60 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" />{item.attachments.length} attachment{item.attachments.length > 1 ? "s" : ""}</p>}
        </div>
      </div>
    </article>
  );
}

export default function AgentHome({ preview = false }) {
  const { user, currentAccount, viewAs } = useAuth();
  const navigate = useNavigate();
  const open = useOpenContent(preview);
  const [feed, setFeed] = useState(null);
  const [settings, setSettings] = useState({});
  const [layout, setLayout] = useState(null);
  const [editing, setEditing] = useState(false);
  const q = preview ? "?preview=agent" : "";
  useEffect(() => {
    api.get("/content/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [currentAccount]);
  const loadLayout = () => { api.get("/training-center/agent_home").then((r) => setLayout(r.data)).catch(() => setLayout(null)); };
  useEffect(() => { loadLayout(); }, [currentAccount]);
  useEffect(() => {
    api.get(`/content/feed${q}`).then((r) => setFeed(r.data)).catch(() => setFeed({ announcements: [], recognition: [], training: [], resources: [], archive_total: 0 }));
  }, [q, currentAccount]);
  const first = (user?.first_name || user?.name || "").split(" ")[0];
  const f = feed || {};
  const secs = (layout?.design?.sections || DEFAULT_SECTIONS).filter((s) => !s.hidden);
  const head = (s, d) => ({ kicker: s.kicker ?? d.kicker, title: s.title || d.title, intro: s.intro ?? d.intro ?? "" });
  const render = (s, idx) => {
    const next = secs[idx + 1];
    const pair = (s.key === "news" && next?.key === "recognition") || (s.key === "recognition" && next?.key === "news");
    const prev = secs[idx - 1];
    if ((s.key === "news" && prev?.key === "recognition") || (s.key === "recognition" && prev?.key === "news")) return null;
    const one = (x, wrap) => SECTIONS[x.key]?.(x, wrap);
    if (pair) return <div key={s.key} className="mt-10 grid gap-10 lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)]">{one(s, false)}{one(next, false)}</div>;
    return <React.Fragment key={s.key}>{one(s, true)}</React.Fragment>;
  };
  const SECTIONS = {
    news: (s, wrap) => { const h = head(s, { kicker: "This week", title: "News & Announcements" }); return (
      <section className={`min-w-0 ${wrap ? "mt-12" : ""}`} data-testid="agent-news" key="news">
        <SectionHead kicker={h.kicker} title={h.title} intro={h.intro} action="All news" onAction={() => navigate(`/library${q ? q + "&" : "?"}type=announcement,article`)} testId="news-all" />
        {f.announcements?.length ? <div>{f.announcements.map((i, n) => <NewsRow key={i.id} item={i} open={open} lead={n === 0} />)}</div>
          : <QuietEmpty icon={Megaphone} text="No new announcements right now — you're all caught up." testId="news-empty" />}
      </section>); },
    recognition: (s, wrap) => { const h = head(s, { kicker: "Celebrating", title: "Recognition" }); return (
      <section className={`min-w-0 ${wrap ? "mt-12" : ""}`} data-testid="agent-recognition" key="recognition">
        <SectionHead kicker={h.kicker} title={h.title} intro={h.intro} action="Hall of fame" onAction={() => navigate(`/library${q ? q + "&" : "?"}type=recognition`)} testId="recognition-all" />
        {f.recognition?.length ? <div className="flex sm:grid sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2 gap-3 overflow-x-auto snap-x pb-1 -mx-1 px-1">{f.recognition.map((i) => <RecognitionCard key={i.id} item={i} open={open} />)}</div>
          : <QuietEmpty icon={Award} text="Achievements, appointments and milestones will be celebrated here." testId="recognition-empty" />}
      </section>); },
    grow: (s) => { const h = head(s, { kicker: "Agent Training Center", title: "Grow Your Agency", intro: "Practical tools, sales strategies and Farmers resources to help you grow, retain customers and build a stronger agency." }); return f.grow?.length > 0 ? (
      <section className="mt-12" data-testid="agent-grow">
        <div className="mb-5 max-w-3xl">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-brand/80">{h.kicker}</p>
          <h2 className="font-editorial text-xl md:text-2xl text-slate-900 leading-tight" data-testid="agent-grow-title">{h.title}</h2>
          {h.intro && <p className="mt-1 text-sm md:text-[15px] text-slate-500 break-words" data-testid="agent-grow-intro">{h.intro}</p>}
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">{f.grow.map((i) => <GrowCard key={i.id} item={i} open={(it) => open(it, { from: "dashboard" })} />)}</div>
      </section>) : null; },
    training: (s) => { const h = head(s, { kicker: "Keep growing", title: "Training" }); return (
      <section className="mt-12" data-testid="agent-training">
        <SectionHead kicker={h.kicker} title={h.title} intro={h.intro} action="Open the Training library" onAction={() => navigate("/training")} testId="training-all" />
        {f.training?.length ? <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">{f.training.map((i) => <TrainingCard key={i.id} item={i} open={open} />)}</div>
          : <QuietEmpty icon={GraduationCap} text="New training videos and sessions will be featured here." testId="training-empty" />}
      </section>); },
    resources: (s) => { const h = head(s, { kicker: "Toolkit", title: "Resources" }); return (
      <section className="mt-12" data-testid="agent-resources">
        <SectionHead kicker={h.kicker} title={h.title} intro={h.intro} action="All resources" onAction={() => navigate(`/library${q ? q + "&" : "?"}type=resource`)} testId="resources-all" />
        {f.resources?.length ? <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{f.resources.map((i) => <ResourceTile key={i.id} item={i} open={open} />)}</div>
          : <QuietEmpty icon={FolderOpen} text="Guides, forms and reference documents will be collected here." testId="resources-empty" />}
      </section>); },
  };
  return (
    <div className="max-w-[1240px] mx-auto" data-testid={preview ? "agent-dashboard-preview" : "agent-home"}>
      {preview && (
        <div className="mb-5 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3" data-testid="agent-preview-bar">
          <Eye className="w-5 h-5 text-sky-700 shrink-0" />
          <p className="text-sm text-sky-900 flex-1">You're seeing the shared Agent experience for your groups — content only, no individual agent's information. To see exactly what one agent sees, use <b>View As → Agent</b> and pick the person.</p>
        </div>
      )}
      {(layout?.design_groups?.length > 0 || layout?.all_scope) && !viewAs && (
        <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
          {layout.all_scope && <p className="text-xs text-slate-500 mr-auto" data-testid="dash-all-scope">Viewing All my districts — standard layout. Pick a District to see its own Dashboard layout.</p>}
          {layout.design_groups?.length > 0 && <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="dash-sections-btn"><LayoutList className="w-4 h-4 mr-1" />Edit Dashboard Sections</Button>}
        </div>
      )}
      <Masthead logo={settings.logo_url} name={viewAs?.person_name?.split(" ")[0] || first} account={currentAccount?.account_name} preview={preview} />
      {feed === null ? <div className="h-[380px] rounded-3xl bg-slate-100 animate-pulse" /> : <Leadership item={f.leadership} open={open} />}

      {secs.map(render)}
      {editing && layout && <DashboardSectionsEditor page={layout} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); loadLayout(); }} />}

      <section className="mt-12 mb-4">
        <ArchiveTeaser total={f.archive_total || 0} onOpen={() => navigate(`/library${q}`)} />
      </section>
      {!preview && <CandidateSupportWidget />}
    </div>
  );
}
